import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { Flota, FlotasPagingRes } from '@/shared/interfaces/erp/mantenimiento-operaciones';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchFlotas = (params?: GetFlotasParams, enabled = true) => {
  return useQuery({
    queryKey: ['flotas', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getFlotasAll(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};
export const useFetchFlotasNoState = (params?: GetFlotasParams) => {
  return useQuery({
    queryKey: ['flotasNoState', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getFlotasNoState(params);
    },
  });
};

export const useGetFlota = (id: string) => {
  return useQuery({
    queryKey: ['flotas', id],
    queryFn: async () => {
      return getFlota(id);
    },
    retry: false,
  });
};

export const useCreateFlota = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createFlota,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flotas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Flota creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateFlota = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateFlota,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['flotas'],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Flota actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetFlotasParams = Omit<Partial<Flota>, | 'zonas'> & {
  page?: number;
  page_size?: number;
  zonas?:string;

  // helper state
  filterByState?: boolean;
  notFilterByState?: boolean;
};

export type CreateFlotaParams = Omit<Partial<Flota>, 'id_flota' | 'user_data'> & {
  user: number;
  //zonas_select?:Zone[];
};
export type UpdateFlotaParams = {
  id: string;
  data: CreateFlotaParams | Partial<Flota>;
};

export const getFlotasAll = async (params?: GetFlotasParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<FlotasPagingRes>(
    `${urlBackend}api/v1/flota/?${queryParams}`,
    true
  );
};
export const getFlotasNoState = async (params?: GetFlotasParams) => {
  if (params) {
    delete params.notFilterByState;
    delete params.estado;
  }

  return get<FlotasPagingRes>(
    `${urlBackend}api/v1/flota/?${getUrlParamsFetch(params ?? {})}`,
    true
  );
};

export const getFlota = async (id: string) => {
  try {
    return await get<Flota>(`${urlBackend}api/v1/flota/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createFlota = async (data: CreateFlotaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Flota>(`${urlBackend}api/v1/flota/`, data, true);
};

export const updateFlota = async ({ id, data }: UpdateFlotaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Flota>(`${urlBackend}api/v1/flota/${id}/`, data, true);
};